import i18next from 'i18next';

import {__isKeyAvailable, cookieHelper, gistApi, requestHelper, responseHelper} from '../_helpers';
import {cookieIdentifier} from "../_constants";

const userSessionIdentifier = cookieIdentifier.USER_SESSION;
const userInfoIdentifier = cookieIdentifier.USER_INFO;
const userSubscriptionIdentifier = cookieIdentifier.USER_SUBSCRIPTION;

/**
 * This service adds the following functionality
 */
export const userService = {
    login,
    logout,
    createAccount,
    validateAccount,
    fetchUser,
    getAuthUser,
    getUserDetails,
    setPayment,
    refreshLogin,
    generatePasswordResetToken,
    setNewPassword,
    changePassword,
    cancelAccount,
    isConnectedToZoom
};

/**
 * This function logs in the user
 * @param username
 * @param password
 * @returns {Promise<{username: *, token: *}>}
 */
function login(username, password) {
    let myHeader = requestHelper.loginHeader();
    let data = requestHelper.prepOAuth(username, password);
    let link = '/oauth/token';
    const t = i18next.t;

    return gistApi.post(link, data, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(user => {
            let userReturn = {
                'username': username,
                'token': user.access_token,
                'refreshToken': user.refresh_token
            };

            // save the user for authentication
            cookieHelper.saveStorageData(userSessionIdentifier, userReturn);
            return {'username': username};  // don't save tokens in redux
        })
        .catch(error => {
            logout();
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 400:
                        res.errorMsg = t("Sorry, that password isn't right.");
                        break;
                    case 401:
                        res.errorMsg = t("Sorry, we couldn't find an account with that email.");
                        break;
                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        });
}

/**
 * This function creates a new account based on the given username and password
 * @param username
 * @param password
 * @param plan
 * @param paymentToken
 * @returns {Promise<{username: *, token: *}>}
 */
function createAccount(username, password, plan, paymentToken) {
    const t = i18next.t;

    if (!username) {
        return Promise.reject({errorMsg: t("Sorry, looks like that isn't a valid email address.")} );
    }
    if (!password || password.length < 6) {
        return Promise.reject( {errorMsg: t("Sorry, password needs to be at least 6 characters.")} );
    }

    const myHeader = requestHelper.queryHeader();
    const user = {
        "email": username,
        "password": password,
        "plan": plan,
        "paymentToken": paymentToken,
    };
    const link = '/api/users/';

    return gistApi.post(link, user, {headers: myHeader})
        .then((response) => {
            return responseHelper.handleResponse(response)
        })
        .then(user => {
            return {
                'username': user.userName,
                'email': user.email,
                'role': user.role
            };
        })
        .catch((error) => {
            logout();
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status =error.response.status;
                switch (error.response.status) {
                    case 400:
                        res.errorMsg = t("Sorry, looks like that isn't a valid email address.");
                        break;
                    case 409:
                        res.errorMsg = t("Looks like there's an account with this email address already.");
                        break;
                    default:
                        break;
                }
            }
            return Promise.reject(res);
        })
}

/**
 * This function creates a new account based on the given username and password
 * @param username
 * @param password
 * @returns {Promise<{username: *, token: *}>}
 */
function validateAccount(username, password) {
    const t = i18next.t;

    if (!username) {
        return Promise.reject({errorMsg: t("Sorry, looks like that isn't a valid email address.")} );
    }
    if (!password || password.length < 6) {
        return Promise.reject( {errorMsg: t("Sorry, password needs to be at least 6 characters.")} );
    }

    const myHeader = requestHelper.queryHeader();
    const user = {
        "email": username,
        "password": password,
    };
    const link = '/api/users/validation';

    return gistApi.post(link, user, {headers: myHeader})
        .then((response) => {
            return responseHelper.handleResponse(response)
        })
        .then(user => {
            return {
                'username': user.userName,
                'email': user.email,
                'role': user.role
            };
        })
        .catch((error) => {
            logout();
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status =error.response.status;
                switch (error.response.status) {
                    case 400:
                        res.errorMsg = t("Sorry, looks like that isn't a valid email address.");
                        break;
                    case 409:
                        res.errorMsg = t("Looks like there's an account with this email address already.");
                        break;
                    default:
                        break;
                }
            }
            return Promise.reject(res);
        })
}

/**
 * This method refreshes the login based on refresh_token
 */
function refreshLogin() {
    let myHeader = requestHelper.loginHeader();

    // get username and refresh token from authentication storage
    let userTokens = cookieHelper.getStorageData(userSessionIdentifier);
    let data = requestHelper.prepOAuthRefresh(userTokens.username, userTokens.refreshToken);
    let link = '/oauth/token';

    return gistApi.post(link, data, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(user => {
            let userReturn = {
                'username': userTokens.username,
                'token': user.access_token,
                'refreshToken': user.refresh_token
            };

            // save the user to the authentication storage
            cookieHelper.saveStorageData(userSessionIdentifier, userReturn);
            return userReturn;
        })
        .catch(error => {
            //  refresh token task has failed, so remove tokens from authentication storage
            let userReturn = {
                'username': userTokens.username
            };
            cookieHelper.saveStorageData(userSessionIdentifier, userReturn);
            return Promise.reject( error );
        });
}

/**
 * This function removes the authenticated user from the authentication storage
 */
function logout() {
    return Promise.all([
        cookieHelper.removeStorageData(userSessionIdentifier),
        cookieHelper.removeStorageData(userInfoIdentifier),
        cookieHelper.removeStorageData(userSubscriptionIdentifier),
    ]).then(() => {
        return Promise.resolve();
    }).catch(() => {
        return Promise.reject("Cookie cleanup failed.")
    });
}

/**
 * Returns the current user auth session
 * @returns {any}
 */
function getAuthUser() {
    return cookieHelper.getStorageData(userSessionIdentifier);
}

/**
 * Returns the current user information
 */
function getUserDetails() {
    return cookieHelper.getStorageData(userInfoIdentifier);
}

/**
 * @returns {boolean} indicates if the current user is connected to the Zoom integration
 */
function isConnectedToZoom(userInfo={}) {
    const userDetails = (__isKeyAvailable(userInfo, 'info','details'))
                            ? userInfo.info.details     // use store parameter
                            : getUserDetails();         // use storage
    return (userDetails && userDetails.integrations && userDetails.integrations.includes('ZOOM') );
}

/**
 * This function fetches the current user details
 * @returns {Promise<{email: *, integrations: *}>}
 */
function fetchUser() {
    const myHeader = requestHelper.authHeader();
    const link = '/api/users';

    return gistApi.get(link, {headers: myHeader})
        .then((response) => {
            return responseHelper.handleResponse(response)
        })
        .then(user => {
            // parse the user and include necessary fields
            const data = {
                "email": user.email,
                "integrations": user.integrations
            };
            cookieHelper.saveStorageData(userInfoIdentifier, data);
            return data;
        })
        .catch((error) => {
            logout();
            return Promise.reject( responseHelper.handleError(error.response) );
        })
}

/**
 * This function updates the user's payment information
 * @returns {Promise}
 */
function setPayment(stripeToken) {
    const myHeader = requestHelper.authHeader();
    const link = '/api/users/payment';
    const t = i18next.t;

    return gistApi.post(link, {id: stripeToken.id}, {headers: myHeader})
        .then((response) => {
            return responseHelper.handleResponse(response)
        })
        .then(data => {
            return data;
        })
        .catch((error) => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 401:
                        res.errorMsg = t("Sorry, you are not authorized to update payment information.");
                        break;

                    case 400:
                        let errorData = error.response.data;
                        if( !errorData.sourceStatus ){
                            // the source was never added to the account
                            res.errorMsg = t("Sorry, we weren't able to update your payment information. Please try again.");

                        } else if ( !errorData.paymentStatus ) {
                            // the source was added but payment was not successful
                            res.errorMsg = t("Uh oh! We weren’t able to validate that card. Are you sure the information is correct?");
                        }
                        break;

                    default:
                        break;
                }
                return Promise.reject(res);
            }
        })
}

/**
 * This function passes instructions for generating tokens for password change
 * @param username the email of the user
 * @returns {Promise<never | never>}
 */
function generatePasswordResetToken(username) {
    let myHeader = requestHelper.queryHeader();
    let link = '/api/users/password/generation';
    const t = i18next.t;

    return gistApi.post(link, {"email": username}, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 400:
                        res.errorMsg = t("Sorry, looks like that isn't a valid email address.");
                        break;
                    case 404:
                        res.errorMsg = t("Sorry, we couldn't find an account with that email.");
                        break;
                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        });
}

/**
 * This function passes credentials for password change
 * @param username the email of the account
 * @param token the unique 'validity-limited' token for password reset
 * @param password the new password
 * @returns {Promise<never | never>}
 */
function setNewPassword(username, token, password){
    let myHeader = requestHelper.queryHeader();
    let link = '/api/users/password';
    const t = i18next.t;

    const data = {
        "email": username,
        "token": token,
        "password": password,
    };

    return gistApi.post(link, data, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 400:
                        res.errorMsg = t("Sorry, the email seems to be incorrect. Please use the unique password reset link.");
                        break;
                    case 404:
                        res.errorMsg = t("Sorry, the token seems to be incorrect. Please use the unique password reset link.");
                        break;
                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        });
}

/**
 * This function changes password; accepts current password and new password
 * @param oldPassword current password to change
 * @param newPassword new password to be change to
 * @returns {Promise<never | never>}
 */
function changePassword(oldPassword, newPassword){
    const myHeader = requestHelper.authHeader();
    let link = '/api/users/password';
    const t = i18next.t;

    const data = {
        "old_password": oldPassword,
        "new_password": newPassword,
    };

    return gistApi.put(link, data, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 401:
                        res.errorMsg = t("Sorry, the password does not seem to match. Please use your correct current password.");
                        break;
                    case 409:
                        res.errorMsg = t("Sorry, the old and new password seems to be the same. Please use a different new password.");
                        break;
                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        });
}

/**
 * This function cancels the current user's account
 *
 * @returns {Promise<never | never>}
 */
function cancelAccount(){
    const myHeader = requestHelper.authHeader();
    let link = '/api/users';
    const t = i18next.t;

    return gistApi.delete(link, {headers: myHeader})
      .then(response => {
          logout(); // remove the user tokens
          return responseHelper.handleResponse(response)
      })
      .catch(() => {
          let res = {
              "status": 500,
              "errorMsg": t("Sorry, we're having technical issues. Please try again.")
          };
          return Promise.reject( res );
      });
}